import { useEffect, useRef, useState } from 'react';

// Define types for the hook options
interface SwipeOptions {
  minSwipeDistance?: number;
  maxVerticalOffset?: number;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
}

// Define types for touch positions
interface TouchPosition {
  x: number;
  y: number;
}

// Define the return type
type SwipeDirection = 'left' | 'right' | null;

export function useSwipeDetection(options: SwipeOptions = {}): SwipeDirection {
  const [swipeDirection, setSwipeDirection] = useState<SwipeDirection>(null);
  const touchStartRef = useRef<TouchPosition | null>(null);
  const touchEndRef = useRef<TouchPosition | null>(null);

  // Default options
  const {
    minSwipeDistance = 50,
    maxVerticalOffset = 100,
    onSwipeLeft = () => {},
    onSwipeRight = () => {},
  } = options;

  useEffect(() => {
    const handleTouchStart = (e: TouchEvent): void => {
      touchStartRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY
      };
      touchEndRef.current = null;
    };

    const handleTouchMove = (e: TouchEvent): void => {
      touchEndRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY
      };
    };

    const handleTouchEnd = (): void => {
      if (!touchStartRef.current || !touchEndRef.current) return;

      const horizontalDistance = touchEndRef.current.x - touchStartRef.current.x;
      const verticalDistance = Math.abs(touchEndRef.current.y - touchStartRef.current.y);

      // Only count as swipe if vertical movement is limited
      if (verticalDistance <= maxVerticalOffset) {
        // Determine direction and check minimum distance
        if (horizontalDistance > minSwipeDistance) {
          setSwipeDirection('right');
          onSwipeRight();
        } else if (horizontalDistance < -minSwipeDistance) {
          setSwipeDirection('left');
          onSwipeLeft();
        }
      }

      // Reset
      touchStartRef.current = null;
      touchEndRef.current = null;
    };

    // Add event listeners
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd);

    // Clean up
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [minSwipeDistance, maxVerticalOffset, onSwipeLeft, onSwipeRight]);

  return swipeDirection;
};